import { z } from "zod";

// ---------------------------------------------------------------------------
// 見積の区分 2回目：拾い出し（積算）の表（#130、docs/47 §1 の 4・11、§2.2、DB 20261003_2330）。
// 拾い出しは見積の行とは別の表（estimate_takeoffs）。1行＝場所・式（12.5×9.6）・数量・単位・メモ・写真。
// 行の数量の出どころが takeoff のとき、行の数量＝ぶら下がる拾い出しの合計（DB が決める。画面・API は書かない）。
//
// 式の評価は DB の fn_takeoff_eval と ここの evalTakeoff の2か所。**同じ決まりに保つ**（片方を変えたら両方変え、
// TAKEOFF_EVAL_CASES の 40 本で同じ答えになることを Vitest と db/test_estimate_takeoffs.sql で確かめる）
// ---------------------------------------------------------------------------

/** 行の数量の出どころ（document_lines.quantity_source）。manual＝手で打つ／takeoff＝拾い出しの合計 */
export const QuantitySource = z.enum(["manual", "takeoff"]);
export type QuantitySource = z.infer<typeof QuantitySource>;

/** 一式の印（document_lines.lumpsum_basis）。単位が「式」のふつうの行だけ持つ（ほかは null） */
export const LumpsumBasis = z.enum(["breakdown", "memo", "none"]);
export type LumpsumBasis = z.infer<typeof LumpsumBasis>;

/** 画面に出す文（docs/10 §5.1.2）。決まり文はここ1か所 */
export const TAKEOFF_MSG = {
  /** 言葉 {takeoff}（拾い出し／数量の内訳。#142 段3）＝画面は t() で */
  takeoff: "{takeoff}",
  formula: "式",
  /** #316（11/5）：引き出しの入力の2段目の主ボタン（前は「＋ 拾い出し」） */
  add: "＋ 追加",
  /** 引き出しの頭の ×（読み上げ・乗せたとき） */
  close: "閉じる",
  /** 頭の下の帯「合計 137.01 ㎡　3行」 */
  total: "合計",
  count: "{n}行",
  /** この行の拾い出しが無いとき（前は「まだありません。下の欄から足します。」） */
  none: "拾い出しなし",
  /** 行を選ばずに開いたとき（行に入れていない拾い出しだけ） */
  looseOnly: "行に入れていないもの",
  /** {n}＝件数 */
  unassigned: "行に入れていない拾い出し {n}件",
  /** {n}＝行数、{yen}＝金額（桁区切り） */
  lumpsumWarning: "内訳のない一式 {n}行 {yen}円",
  lumpsum: { breakdown: "一式：内訳あり", memo: "一式：根拠あり", none: "一式：内訳なし" } satisfies Record<LumpsumBasis, string>,
  toManual: "手入力に切替",
  toTakeoff: "拾い出し合計に切替",
  attach: "この行に追加",
  detach: "行から除外",
  addChild: "内訳行追加",
  /** #142 段6 No.29：紙の題は言葉の {takeoffSheet}（PDF は会社の言葉で埋める） */
  sheetTitle: "{takeoffSheet}",
  noFormula: "式が読めません",
  /** API のエラー */
  notFound: "その拾い出しは見つかりません",
  unitMismatch: "単位の違う拾い出しは同じ行に入れられません（合計できません）",
  lineInvalid: "その行はこの見積にありません",
  lineNotDetail: "拾い出しを入れられるのは、ふつうの行だけです",
  placeInvalid: "その場所はこの見積のものではありません",
  empty: "式かメモを入れてください",
  photoType: "写真は JPEG だけです",
  photoTooBig: "写真が大きすぎます（長辺 1600px・2MB まで。画面で縮めてから送ります）",
  photoBroken: "この写真は読めませんでした",
} as const;

/** 写真の決まり（表示用 1枚だけ。画面で縮めてから送る＝サーバーでは縮めない） */
export const TAKEOFF_PHOTO = { maxPx: 1600, maxBytes: 2 * 1024 * 1024, quality: 0.8 } as const;

/* ---------------- 式の評価（DB の fn_takeoff_eval と同じ決まり） ---------------- */

/** 全角 → 半角（数字・小数点・＋・マイナス3種と長音・×＊・÷／・括弧・全角の空白・タブ）。DB の translate と同じ並び */
const FROM = "０１２３４５６７８９．＋－−ー×＊÷／（）　\t";
const TO = "0123456789.+---**//()  ";
const MAP = new Map<string, string>([...FROM].map((c, i) => [c, TO[i]!]));
const normalize = (formula: string): string => [...formula].map((c) => MAP.get(c) ?? c).join("");

type Frac = { n: bigint; d: bigint };
const TEN_K = 10000n;
const LIMIT = 100000000000000n; // numeric(14,4) に入らない（10^10 以上）

/** 式 → 小数4桁に丸めた 1万倍の整数（読めなければ null） */
function evalScaled(formula: string): bigint | null {
  const s = normalize(formula);
  if (s.length > 200 || !/^[0-9.+*/() -]+$/.test(s)) return null;
  type Tok = { k: "n"; v: Frac } | { k: "+" | "-" | "*" | "/" | "u" };
  const out: Tok[] = [];
  const ops: string[] = [];
  let want = true; // 次に来るのは数（か、単項の符号・開き括弧）
  let i = 0;
  while (i < s.length) {
    const ch = s[i]!;
    if (ch === " ") {
      i++;
    } else if (/[0-9.]/.test(ch)) {
      if (!want) return null;
      const tok = /^[0-9.]+/.exec(s.slice(i))![0];
      if (!/^([0-9]+\.?[0-9]*|\.[0-9]+)$/.test(tok)) return null;
      const dot = tok.indexOf(".");
      out.push({ k: "n", v: { n: BigInt(tok.replace(".", "") || "0"), d: dot < 0 ? 1n : 10n ** BigInt(tok.length - dot - 1) } });
      i += tok.length;
      want = false;
    } else if (ch === "(") {
      if (!want) return null;
      ops.push(ch);
      i++;
    } else if (ch === ")") {
      if (want) return null;
      for (;;) {
        const top = ops.pop();
        if (top === undefined) return null; // 開き括弧が無い
        if (top === "(") break;
        out.push({ k: top as "+" });
      }
      i++;
    } else if (want) {
      // 単項の符号（-1.8*2・2*-3）。＋は何もしない
      if (ch === "-") ops.push("u");
      else if (ch !== "+") return null;
      i++;
    } else {
      // 2項の演算子：山の上が同じか強い間は先に出す（左から計算）
      for (;;) {
        const top = ops[ops.length - 1];
        if (top === undefined || top === "(" || ((top === "+" || top === "-") && (ch === "*" || ch === "/"))) break;
        ops.pop();
        out.push({ k: top as "+" });
      }
      ops.push(ch);
      i++;
      want = true;
    }
  }
  if (want) return null; // 空・演算子で終わる
  for (;;) {
    const top = ops.pop();
    if (top === undefined) break;
    if (top === "(") return null; // 閉じ括弧が無い
    out.push({ k: top as "+" });
  }
  // 逆ポーランドを分数のまま計算する（割り切れない割り算でも DB と1桁も違わない）
  const st: Frac[] = [];
  for (const t of out) {
    if (t.k === "n") {
      st.push(t.v);
    } else if (t.k === "u") {
      const a = st[st.length - 1];
      if (!a) return null;
      st[st.length - 1] = { n: -a.n, d: a.d };
    } else {
      const b = st.pop();
      const a = st.pop();
      if (!a || !b) return null;
      if (t.k === "+") st.push({ n: a.n * b.d + b.n * a.d, d: a.d * b.d });
      else if (t.k === "-") st.push({ n: a.n * b.d - b.n * a.d, d: a.d * b.d });
      else if (t.k === "*") st.push({ n: a.n * b.n, d: a.d * b.d });
      else {
        if (b.n === 0n) return null; // 0 で割る
        st.push({ n: a.n * b.d * (b.n < 0n ? -1n : 1n), d: a.d * (b.n < 0n ? -b.n : b.n) });
      }
    }
  }
  if (st.length !== 1) return null;
  const { n, d } = st[0]!;
  const abs = n < 0n ? -n : n;
  // 小数4桁へ四捨五入（0 から遠い方へ）：floor(|x|×10^4 ＋ 1/2)
  const q = (abs * 2n * TEN_K + d) / (d * 2n);
  if (q >= LIMIT) return null;
  return n < 0n ? -q : q;
}

/**
 * 拾い出しの式 → 数量（小数4桁）。数字・小数点・＋−×÷（全角・半角・* /）・括弧・空白だけ。
 * 読めない式・0 割り・大きすぎる答えは null。DB の fn_takeoff_eval と同じ答え
 */
export function evalTakeoff(formula: string): number | null {
  const q = evalScaled(formula);
  return q == null ? null : Number(q) / 10000;
}

/** 行の数量＝拾い出しの合計（DB の fn_takeoff_total と同じ）：小数2桁に四捨五入し、0 より小さくしない。数量の無い拾い出しは数えない */
export function takeoffTotal(quantities: Array<number | null>): number {
  let sum = 0n;
  for (const q of quantities) if (q != null) sum += BigInt(Math.round(q * 10000));
  if (sum < 0n) sum = 0n;
  return Number((sum + 50n) / 100n) / 100;
}

/** 式を紙・画面に出す形に（半角にして × ÷ に。読めない文字はそのまま） */
export function prettyFormula(formula: string): string {
  return normalize(formula).trim().replace(/\s+/g, " ").replace(/\*/g, "×").replace(/\//g, "÷");
}

/** 数量の出し方（小数4桁まで。うしろの 0 は出さない）：120・9.72・1.5625 */
export function takeoffQty(q: number | null): string {
  if (q == null) return "";
  return String(Math.round(q * 10000) / 10000);
}

/**
 * 式 40 本と答え（null＝読めない）。Vitest（takeoff.test.ts）と SQL（db/test_estimate_takeoffs.sql ①）が同じ並びを確かめ、
 * API のテストが DB の fn_takeoff_eval と evalTakeoff の答えを1本ずつ突き合わせる
 */
export const TAKEOFF_EVAL_CASES: ReadonlyArray<readonly [string, number | null]> = [
  ["12.5*9.6", 120],
  ["(3.6+2.4)*2.4", 14.4],
  ["１２．５×９．６", 120],
  ["3.6×2.7", 9.72],
  ["2.7×2.7", 7.29],
  ["10÷4", 2.5],
  ["10/3", 3.3333],
  ["2/3", 0.6667],
  ["1/3*3", 1],
  ["1÷3×3×0.00005", 0.0001],
  ["-1.8*2", -3.6],
  ["12.5*2.4-1.8*2", 26.4],
  ["−1.8×2.0", -3.6],
  ["－３＋５", 2],
  ["2*-3", -6],
  ["--2", 2],
  ["+5", 5],
  ["(1+2)*(3+4)", 21],
  ["2+3*4", 14],
  ["5/2/2", 1.25],
  ["2-3-4", -5],
  ["1.5+2.25+3.125", 6.875],
  ["0.1+0.2", 0.3],
  [".5*4", 2],
  ["12.*2", 24],
  [" 12.5 × 9.6　", 120],
  ["（３．６＋２．４）＊２．４", 14.4],
  ["3ー1", 2],
  ["1.23456", 1.2346],
  ["1.23454", 1.2345],
  ["-0.00005", -0.0001],
  ["9999999999.9999", 9999999999.9999],
  ["100000*100000", null],
  ["1/0", null],
  ["1/(2-2)", null],
  ["12.5x9.6", null],
  ["12.5*", null],
  ["(1+2", null],
  ["1 2", null],
  ["", null],
];

/* ---------------- 拾い出しの行 ---------------- */

export const Takeoff = z.object({
  id: z.string().uuid(),
  /** いま開いている回の行の id（行に入れていない・その行がいまの回に無いときは null） */
  lineId: z.string().uuid().nullable(),
  /** 行の系譜（estimate_takeoffs.line_lineage_id）。null＝まだ行に入れていない */
  lineageId: z.string().uuid().nullable(),
  /** 場所（その見積の estimate_places） */
  placeId: z.string().uuid().nullable(),
  formula: z.string(),
  /** 式の答え（DB の fn_takeoff_eval。読めない式は null） */
  quantity: z.number().nullable(),
  unit: z.string(),
  memo: z.string(),
  /** 写真（表示用 1600px）。無ければ null */
  photoUrl: z.string().nullable(),
  clientUuid: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Takeoff = z.infer<typeof Takeoff>;

const Formula = z.string().max(200);
const Unit = z.string().trim().max(20);
const Memo = z.string().trim().max(200);

/** POST /estimates/:id/takeoffs。行は lineId（いまの回の行の id）か lineLineageId（系譜）で指す。どちらも無ければ「行に入れていない拾い出し」 */
export const TakeoffCreate = z.object({
  lineId: z.string().uuid().nullable().optional(),
  lineLineageId: z.string().uuid().nullable().optional(),
  placeId: z.string().uuid().nullable().default(null),
  formula: Formula.default(""),
  /** 省くと、入れる行の単位 */
  unit: Unit.optional(),
  memo: Memo.default(""),
  /** オフライン同期用（スマホが決める）。同じ番号を送り直しても2つにならない */
  clientUuid: z.string().uuid().optional(),
});
export type TakeoffCreate = z.infer<typeof TakeoffCreate>;

/** PUT /estimates/:id/takeoffs/:tid。省いた欄は変えない。lineId: null＝行から外す */
export const TakeoffUpdate = z.object({
  lineId: z.string().uuid().nullable().optional(),
  lineLineageId: z.string().uuid().nullable().optional(),
  placeId: z.string().uuid().nullable().optional(),
  formula: Formula.optional(),
  unit: Unit.optional(),
  memo: Memo.optional(),
});
export type TakeoffUpdate = z.infer<typeof TakeoffUpdate>;

export const TakeoffList = z.object({
  /** 行ごと（lineId あり）と 行に入れていないもの（lineId が null）。並びは作った順 */
  items: z.array(Takeoff),
  unassignedCount: z.number().int(),
});
export type TakeoffList = z.infer<typeof TakeoffList>;

/** Estimate.takeoffs：行の系譜ごとの件数と合計、行に入れていない件数 */
export const TakeoffSummary = z.object({
  lines: z.array(
    z.object({
      lineId: z.string().uuid(),
      lineageId: z.string().uuid(),
      count: z.number().int(),
      /** 拾い出しの合計（行の数量に入る値。小数2桁） */
      total: z.number(),
    }),
  ),
  unassigned: z.number().int(),
});
export type TakeoffSummary = z.infer<typeof TakeoffSummary>;

/* ---------------- 一式の印と、社内向けの知らせ ---------------- */

/** 一式の単位（DB の fn_lumpsum_sync と同じ） */
export const isLumpsumUnit = (unit: string | null | undefined): boolean => {
  const u = (unit ?? "").trim();
  return u === "式" || u === "一式";
};

/** 知らせの基準（会社の設定。0＝その基準は使わない） */
export const LumpsumRule = z.object({ pct: z.number().int().min(0).max(100), yen: z.number().int().min(0) });
export type LumpsumRule = z.infer<typeof LumpsumRule>;
export const LUMPSUM_RULE_DEFAULT: LumpsumRule = { pct: 10, yen: 100_000 };

/** Estimate.lumpsumWarnings：内訳のない一式の行（基準を超えたものだけ）と金額の合計。社内用（紙に出ない・出すのは止めない） */
export const LumpsumWarnings = z.object({
  count: z.number().int(),
  total: z.number().int(),
  lineIds: z.array(z.string().uuid()),
  rule: LumpsumRule,
});
export type LumpsumWarnings = z.infer<typeof LumpsumWarnings>;

/**
 * 内訳のない一式（lumpsumBasis＝none）のうち、金額が 税抜合計の pct％ を超えるか yen 円を超える行。
 * 画面（打っている間の金額で）と API（保存した金額で）が同じ関数を使う
 */
export function lumpsumWarningsOf(
  lines: ReadonlyArray<{ id: string; amount: number; lumpsumBasis?: LumpsumBasis | null | undefined }>,
  subtotal: number,
  rule: LumpsumRule,
): LumpsumWarnings {
  const hit = lines.filter((l) => {
    if (l.lumpsumBasis !== "none" || l.amount <= 0) return false;
    const byPct = rule.pct > 0 && subtotal > 0 && l.amount * 100 > subtotal * rule.pct;
    const byYen = rule.yen > 0 && l.amount > rule.yen;
    return byPct || byYen;
  });
  return { count: hit.length, total: hit.reduce((a, l) => a + l.amount, 0), lineIds: hit.map((l) => l.id), rule };
}

/* ---------------- 積算表（PDF の別紙）と、出した回に固める形 ---------------- */

/** 積算表の1行（出すときに template_snapshot.takeoff.rows に固める。行はその回の行の id、場所は名前で持つ） */
export const TakeoffSheetRow = z.object({
  lineId: z.string().uuid(),
  place: z.string(),
  formula: z.string(),
  quantity: z.number().nullable(),
  unit: z.string(),
});
export type TakeoffSheetRow = z.infer<typeof TakeoffSheetRow>;

/** template_snapshot.takeoff：積算表を付けるか（会社の設定を固めた値）と、出した時点の拾い出し */
export const TakeoffSnapshot = z.object({ sheet: z.boolean(), rows: z.array(TakeoffSheetRow) });
export type TakeoffSnapshot = z.infer<typeof TakeoffSnapshot>;
